import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import type { GroupedActionOption, RowActionOption, SelectionInfo } from '@/components/table-rest-api/types';
import { getContact } from '@/composables/data-explorer/DataExplorer';
import { UserState } from '@/composables/User';
import { DataExplorerDataType } from '@/types';
import type { ActionPayload } from './types';

/**
 * Interface for action component refs
 * Using a more permissive type to handle Vue component instance types
 */
interface ActionComponentRef {
  open?: (payload: ActionPayload) => void;
}

/**
 * Options for useContactsActions composable
 */
interface UseContactsActionsOptions {
  copyToListRef: Ref<ActionComponentRef | any>;
  moveToListRef?: Ref<ActionComponentRef | any>;
  createListRef: Ref<ActionComponentRef | any>;
  subscribeRef?: Ref<ActionComponentRef | any>;
  unsubscribeRef?: Ref<ActionComponentRef | any>;
  deleteFromListRef?: Ref<ActionComponentRef | any>;
  deleteFromAllListsRef?: Ref<ActionComponentRef | any>;
  // Facturation V5 : (dé)marquer des contacts comme facturables.
  setBillableRef?: Ref<ActionComponentRef | any>;
  setNonBillableRef?: Ref<ActionComponentRef | any>;
  // RGPD (SC-3851) : effacement définitif des données personnelles d'un contact.
  gdprEraseRef?: Ref<ActionComponentRef | any>;
  listType?: Ref<string>; // 'segment' | 'static' | etc.
}

/**
 * Composable that provides grouped and row actions for contacts list
 * Actions are configured to open the corresponding action components
 */
export default function useContactsActions(options: UseContactsActionsOptions) {
  const { t } = useI18n();
  const store = useStore();

  // Check if list is a segment type
  const isSegmentList = computed(() => options.listType?.value === 'segment');

  // Facturation V5 : les actions facturable/non-facturable n'apparaissent que
  // pour les shops dont l'offre est V5 (bundle_version === 15).
  const isV5 = computed(() => UserState.activeShop?.bundleVersion === 15);

  // RGPD (SC-3851) : effacement définitif, disponible pour tous les profils.
  // Le point de contrôle est ailleurs — action de ligne uniquement (jamais en
  // masse), confirmation par mot tapé, et côté serveur : un seul contact par
  // requête, appel refusé aux clés API, plafonds de 20/minute et 200/heure.
  const canGdprErase = computed(() => true);

  /**
   * Grouped actions - triggered from bulk selection
   * Order: Copy, Move, Create, Unsubscribe, DeleteFromList, DeleteFromAllLists
   * Note: Move, DeleteFromList, DeleteFromAllLists are hidden for segment lists
   */
  const groupedActions = computed<GroupedActionOption[]>(() => {
    const actions: (GroupedActionOption | undefined)[] = [
      // Copy - always included if ref provided
      options.copyToListRef ? {
        label: t('myLists.actions.copy.label'),
        icon: 'fa fa-copy',
        command: (selectionInfo: SelectionInfo) => {
          if (options.copyToListRef.value?.open) {
            options.copyToListRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Move - only if ref provided and not segment list
      options.moveToListRef && !isSegmentList.value ? {
        label: t('myLists.actions.move.label'),
        icon: 'fa fa-arrow-right',
        command: (selectionInfo: SelectionInfo) => {
          if (options.moveToListRef?.value?.open) {
            options.moveToListRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Create - always included if ref provided
      options.createListRef ? {
        label: t('myLists.actions.create.label'),
        icon: 'fa fa-plus',
        command: (selectionInfo: SelectionInfo) => {
          if (options.createListRef.value?.open) {
            options.createListRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Unsubscribe - only if ref provided
      options.unsubscribeRef ? {
        label: t('myLists.actions.unsubscribe.label'),
        icon: 'fa fa-user-slash',
        command: (selectionInfo: SelectionInfo) => {
          if (options.unsubscribeRef?.value?.open) {
            options.unsubscribeRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Delete from list - only if ref provided and not segment list
      options.deleteFromListRef && !isSegmentList.value ? {
        label: t('myLists.actions.deleteFromList.label'),
        icon: 'fa fa-trash',
        command: (selectionInfo: SelectionInfo) => {
          if (options.deleteFromListRef?.value?.open) {
            options.deleteFromListRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Delete from all lists - only if ref provided and not segment list
      options.deleteFromAllListsRef && !isSegmentList.value ? {
        label: t('myLists.actions.deleteFromAllLists.label'),
        icon: 'fa fa-trash-alt',
        command: (selectionInfo: SelectionInfo) => {
          if (options.deleteFromAllListsRef?.value?.open) {
            options.deleteFromAllListsRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Set non-billable (V5) - exclure de la facturation
      options.setNonBillableRef && isV5.value ? {
        label: t('myLists.actions.setNonBillable.label'),
        icon: 'fa fa-ban',
        command: (selectionInfo: SelectionInfo) => {
          if (options.setNonBillableRef?.value?.open) {
            options.setNonBillableRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // Set billable (V5) - réintégrer dans la facturation
      options.setBillableRef && isV5.value ? {
        label: t('myLists.actions.setBillable.label'),
        icon: 'fa fa-circle-check',
        command: (selectionInfo: SelectionInfo) => {
          if (options.setBillableRef?.value?.open) {
            options.setBillableRef.value.open({
              context: 'grouped',
              selectionInfo,
              rowId: null,
            });
          }
        },
      } : undefined,
      // RGPD (SC-3851) : PAS d'action groupée. L'effacement est irréversible et se
      // décide contact par contact — il n'existe qu'en action de ligne (voir
      // `rowActions`). Le serveur refuse d'ailleurs toute requête portant sur plus
      // d'un contact ou en mode « tout sélectionner ».
    ];

    // Filter out undefined actions
    return actions.filter((action): action is GroupedActionOption => action !== undefined);
  });

  /**
   * Row actions - triggered from individual row menu
   * Order: OpenContact, Copy, Move, Create, Subscribe, Unsubscribe, DeleteFromList, DeleteFromAllLists
   */
  const rowActions = computed<RowActionOption[]>(() => {
    const actions: (RowActionOption | undefined)[] = [
      {
        label: t('myLists.actions.openContact.label'),
        icon: 'fa fa-user',
        command: async (rowData) => {
          const openedContactFiles = store.getters['dataExplorer/getOpenedDatas'];
          let contact = openedContactFiles
            .filter((openedContact: any) => openedContact.dataType === DataExplorerDataType.CONTACT_FILE)
            .find((openedContact: any) => openedContact.generalInformation?.data?.idShopContact === rowData.id_contact);
          if (!contact) {
            contact = await getContact(rowData.id_contact);
            console.log('contact', contact);
          }
          if (contact) {
            store.dispatch('dataExplorer/openContactFile', { contact });
          }
        },
        visible: () => true,
      },
      // Copy - always included if ref provided
      options.copyToListRef ? {
        label: t('myLists.actions.copy.label'),
        icon: 'fa fa-copy',
        command: (_rowData, rowKey) => {
          if (options.copyToListRef.value?.open) {
            options.copyToListRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => true,
      } : undefined,
      // Move - only if ref provided and not segment list
      options.moveToListRef ? {
        label: t('myLists.actions.move.label'),
        icon: 'fa fa-arrow-right',
        command: (_rowData, rowKey) => {
          if (options.moveToListRef?.value?.open) {
            options.moveToListRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => !isSegmentList.value,
      } : undefined,
      // Create - always included if ref provided
      options.createListRef ? {
        label: t('myLists.actions.create.label'),
        icon: 'fa fa-plus',
        command: (_rowData, rowKey) => {
          if (options.createListRef.value?.open) {
            options.createListRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => true,
      } : undefined,
      // Subscribe - only if ref provided
      options.subscribeRef ? {
        label: t('myLists.actions.subscribe.label'),
        icon: 'fa fa-user-check',
        command: (_rowData, rowKey) => {
          if (options.subscribeRef?.value?.open) {
            options.subscribeRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => true,
      } : undefined,
      // Unsubscribe - only if ref provided
      options.unsubscribeRef ? {
        label: t('myLists.actions.unsubscribe.label'),
        icon: 'fa fa-user-slash',
        command: (_rowData, rowKey) => {
          if (options.unsubscribeRef?.value?.open) {
            options.unsubscribeRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => true,
      } : undefined,
      // Delete from list - only if ref provided and not segment list
      options.deleteFromListRef ? {
        label: t('myLists.actions.deleteFromList.label'),
        icon: 'fa fa-trash',
        command: (_rowData, rowKey) => {
          if (options.deleteFromListRef?.value?.open) {
            options.deleteFromListRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => !isSegmentList.value,
      } : undefined,
      // Delete from all lists - only if ref provided and not segment list
      options.deleteFromAllListsRef ? {
        label: t('myLists.actions.deleteFromAllLists.label'),
        icon: 'fa fa-trash-alt',
        command: (_rowData, rowKey) => {
          if (options.deleteFromAllListsRef?.value?.open) {
            options.deleteFromAllListsRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => !isSegmentList.value,
      } : undefined,
      // Set non-billable (V5) - n'apparaît que si le contact est ACTUELLEMENT
      // facturable (is_billable=1) ; inutile sur un contact déjà non-facturable.
      options.setNonBillableRef ? {
        label: t('myLists.actions.setNonBillable.label'),
        icon: 'fa fa-ban',
        command: (_rowData, rowKey) => {
          if (options.setNonBillableRef?.value?.open) {
            options.setNonBillableRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: (rowData) => isV5.value && Number(rowData?.is_billable) === 1,
      } : undefined,
      // Set billable (V5) - n'apparaît que si le contact a été exclu manuellement
      // (do_not_charge=1) ; sans effet sinon (déjà facturable, ou injoignable).
      options.setBillableRef ? {
        label: t('myLists.actions.setBillable.label'),
        icon: 'fa fa-circle-check',
        command: (_rowData, rowKey) => {
          if (options.setBillableRef?.value?.open) {
            options.setBillableRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: (rowData) => isV5.value && Number(rowData?.do_not_charge) === 1,
      } : undefined,
      // RGPD (SC-3851) - effacement définitif du contact, quel que soit le type de liste.
      options.gdprEraseRef ? {
        label: t('myLists.actions.gdprErase.label'),
        icon: 'fa fa-user-slash',
        command: (_rowData, rowKey) => {
          if (options.gdprEraseRef?.value?.open) {
            options.gdprEraseRef.value.open({
              context: 'row',
              selectionInfo: null,
              rowId: rowKey,
            });
          }
        },
        visible: () => canGdprErase.value,
      } : undefined,
    ];

    // Filter out undefined actions
    return actions.filter((action): action is RowActionOption => action !== undefined);
  });

  return {
    groupedActions,
    rowActions,
  };
}
